import { Building2, CircleDollarSign, Landmark, LineChart, ShieldCheck, Sparkles, Users, Waypoints, Hammer, Layers, Home, Store } from "lucide-react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const loanPrograms = [
  {
    title: "Fix & Flip Loans",
    icon: Building2,
    description: "Short-term financing for investors acquiring and renovating properties for resale.",
    bullets: ["Built for speed and execution", "Ideal for acquisition plus rehab scenarios", "Structured for transitional projects"],
    terms: { loanRange: "$100K – $3M", term: "6 – 18 months", ltv: "Up to 75% ARV", ltc: "Up to 90%", closingSpeed: "5 – 10 business days" },
    idealFor: "Experienced flippers and value-add investors targeting residential rehab projects.",
    slug: "fix_flip",
  },
  {
    title: "Bridge Loans",
    icon: Landmark,
    description: "Flexible financing for transitional assets, time-sensitive closings, and borrowers moving between acquisition, refinance, or sale.",
    bullets: ["Short-term capital", "Useful for repositioning and timing-driven opportunities", "Built around collateral and timeline"],
    terms: { loanRange: "$150K – $5M", term: "6 – 24 months", ltv: "Up to 70%", closingSpeed: "7 – 14 business days" },
    idealFor: "Investors needing gap funding between acquisition and permanent financing or sale.",
    slug: "bridge",
  },
  {
    title: "DSCR Rental Loans",
    icon: LineChart,
    description: "Long-term financing for income-producing rental properties based on property cash flow rather than traditional personal income.",
    bullets: ["Portfolio-friendly structure", "Built for rental investors", "Supports long-term asset growth"],
    terms: { loanRange: "$75K – $3M", term: "30-year fixed or 5/1 ARM", ltv: "Up to 80%", dscr: "Min 1.0x", closingSpeed: "14 – 21 business days" },
    idealFor: "Buy-and-hold investors with income-generating rental properties (SFR, 2-4 unit).",
    slug: "dscr",
  },
  {
    title: "Cash-Out Refinance",
    icon: CircleDollarSign,
    description: "Unlock equity from investment properties to support reinvestment, liquidity, and strategic expansion.",
    bullets: ["Access capital from stabilized assets", "Useful for growth and recapitalization", "Supports portfolio optimization"],
    terms: { loanRange: "$100K – $5M", term: "12 – 360 months", ltv: "Up to 75%", closingSpeed: "10 – 21 business days" },
    idealFor: "Seasoned investors pulling equity from stabilized properties to fund new acquisitions.",
    slug: "cash_out",
  },
  {
    title: "Construction Loans",
    icon: Hammer,
    description: "Structured financing for major renovation projects and property rehabs with draw schedules and milestone-based funding. For ground-up new builds, see our Additional Programs section below.",
    bullets: ["Draw-based funding aligned to project milestones", "Supports new builds and major repositioning", "Requires detailed project scope and budget"],
    terms: { loanRange: "$250K – $5M+", term: "12 – 24 months", ltc: "Up to 85%", closingSpeed: "14 – 21 business days" },
    idealFor: "Developers and builders executing new construction or heavy renovation projects.",
    slug: "construction",
  },
  {
    title: "Ground Up Development",
    icon: Layers,
    description: "Full-cycle development financing from land acquisition through vertical construction and stabilization.",
    bullets: ["Land acquisition + vertical construction in one package", "Milestone-based draw schedules", "Designed for experienced developers with a clear exit"],
    terms: { loanRange: "$500K – $10M+", term: "18 – 36 months", ltc: "Up to 80%", closingSpeed: "21 – 30 business days" },
    idealFor: "Experienced developers with a track record building residential or mixed-use projects.",
    slug: "ground_up",
  },
  {
    title: "Multifamily Loans",
    icon: Home,
    description: "Financing solutions for 5+ unit multifamily acquisitions, stabilizations, and value-add strategies.",
    bullets: ["Structured for 5+ unit multifamily assets", "Supports stabilized and value-add strategies", "Flexible terms based on property performance and operator experience"],
    terms: { loanRange: "$500K – $10M+", term: "5 – 30 years", ltv: "Up to 75%", dscr: "Min 1.20x", closingSpeed: "21 – 45 business days" },
    idealFor: "Multifamily operators looking to acquire, refinance, or reposition apartment communities.",
    slug: "multifamily",
  },
  {
    title: "Commercial Loans",
    icon: Store,
    description: "Flexible lending solutions for retail, office, mixed-use, and special-purpose commercial real estate assets.",
    bullets: ["Tailored structures for diverse commercial property types", "Income-based and asset-based underwriting approaches", "Designed for acquisitions, refinances, and repositioning"],
    terms: { loanRange: "$250K – $10M+", term: "5 – 25 years", ltv: "Up to 70%", closingSpeed: "21 – 45 business days" },
    idealFor: "Commercial real estate investors targeting office, retail, mixed-use, or industrial properties.",
    slug: "commercial",
  },
];

export const processSteps = [
  { title: "Submit Opportunity", text: "Share your financing scenario, property details, and timing requirements through our borrower intake workflow." },
  { title: "AI + Underwriting Review", text: "We assess the scenario using structured lending criteria, document review, and execution-focused analysis." },
  { title: "Structure Capital", text: "We identify the most suitable path based on product fit, collateral profile, borrower objectives, and timeline." },
  { title: "Move Toward Funding", text: "Qualified scenarios are advanced for lender routing, deal packaging, and next-step execution." },
];

export const capitalBullets = ["Real estate-backed lending opportunities", "Disciplined underwriting standards", "Collateral-aware structures", "Execution-focused deal selection"];
export const borrowerAdvantages = ["Fast underwriting", "Investor-focused lending", "Flexible structures", "Relationship-driven execution"];

export const platformModules = [
  { icon: Sparkles, title: "Borrower Intake Agent", text: "Captures deal scenarios, normalizes submissions, and packages opportunities for review." },
  { icon: ShieldCheck, title: "Underwriting Engine", text: "Supports eligibility logic, risk flags, and execution-oriented scenario analysis." },
  { icon: Waypoints, title: "Lender Routing Layer", text: "Matches borrower opportunities to aligned public and private lending relationships." },
  { icon: Users, title: "Capital Partner Workflows", text: "Supports relationship management for qualified partners and strategic funding channels." },
];
